import React from "react";
import Layout from "../../components/Home/layout";
import GraduateOwl from "../../assets/authLogo1.png";
import FounderPhoto from "../../assets/brand/zach-hill-training.webp";
import AcademyIcon from "../../assets/home/help_1.png";
import StudioIcon from "../../assets/home/help_2.png";
import AdvisoryIcon from "../../assets/home/help_3.png";
import styles from "./styles.module.scss";
import { useTitle } from "../../hooks";

const ACADEMY_URL = "https://academy.traingrc.com/p/home";
const ACADEMY_LOGIN_URL =
  "https://sso.teachable.com/secure/1110292/identity/login/password";

const providers = [
  "AWS",
  "Microsoft Azure",
  "Microsoft 365",
  "Google Cloud",
  "Google Workspace",
];

const learningSteps = [
  {
    number: "01",
    title: "Understand the control",
    copy: "Start with the audit objective, the threat it addresses, and the evidence that matters.",
  },
  {
    number: "02",
    title: "See it in the platform",
    copy: "Follow practitioner-led walkthroughs grounded in the cloud tools your teams actually use.",
  },
  {
    number: "03",
    title: "Practice in the lab",
    copy: "Build confidence with hands-on exercises that turn concepts into repeatable audit skills.",
  },
  {
    number: "04",
    title: "Explain the risk",
    copy: "Leave ready to communicate findings clearly to engineers, leaders, and assurance teams.",
  },
];

const services = [
  {
    eyebrow: "For individual learners",
    title: "Train GRC Academy",
    copy: "Build practical cyber risk and cloud auditing skills through focused courses and hands-on labs.",
    cta: "Explore the Academy",
    href: ACADEMY_URL,
    image: AcademyIcon,
  },
  {
    eyebrow: "For teams and programs",
    title: "Train GRC Studio",
    copy: "Create tailored learning experiences around your technology, controls, and assurance priorities.",
    cta: "Plan custom training",
    href: "https://calendly.com/traingrc/custom-training-services",
    image: StudioIcon,
  },
  {
    eyebrow: "For complex challenges",
    title: "Advisory Services",
    copy: "Get practitioner support for audits, control design, and the decisions that need experienced judgment.",
    cta: "Talk with an advisor",
    href: "https://calendly.com/traingrc/initial-advisory-requests",
    image: AdvisoryIcon,
  },
];

const Home = () => {
  useTitle("Train GRC | Practical Cloud Audit and Cyber Risk Training");

  return (
    <Layout active="home">
      <div className={styles.home}>
        <section className={styles.hero} aria-labelledby="home-heading">
          <div className={styles.heroInner}>
            <div className={styles.heroCopy}>
              <p className={styles.eyebrow}>Join Train GRC Academy</p>
              <h1 id="home-heading">
                Seize Your <span>Expertise.</span>
              </h1>
              <p className={styles.heroLead}>
                Build the cloud auditing and cyber risk skills to assess real
                systems, ask better questions, and explain risk with confidence.
              </p>
              <div className={styles.heroActions}>
                <a className={styles.primaryAction} href={ACADEMY_URL}>
                  Explore courses
                  <span aria-hidden="true">→</span>
                </a>
                <a className={styles.secondaryAction} href="#how-we-help">
                  See how we help
                </a>
              </div>
              <ul className={styles.heroProof} aria-label="Academy highlights">
                <li>Hands-on labs</li>
                <li>Cloud-focused</li>
                <li>Practitioner-led</li>
              </ul>
            </div>

            <div className={styles.heroVisual} aria-label="Train GRC founder and instructor">
              <div className={styles.photoFrame}>
                <img
                  src={FounderPhoto}
                  alt="Zach Hill, Train GRC founder and instructor"
                  width="1200"
                  height="690"
                  fetchpriority="high"
                />
              </div>
              <div className={styles.expertiseCard}>
                <span>Learn it.</span>
                <span>Practice it.</span>
                <strong>Explain it.</strong>
              </div>
              <img
                className={styles.graduateOwl}
                src={GraduateOwl}
                alt=""
                width="180"
                height="221"
              />
            </div>
          </div>
        </section>

        <section className={styles.providerBand} aria-labelledby="platform-heading">
          <div className={styles.providerInner}>
            <p id="platform-heading">
              Practice the platforms you are expected to audit.
            </p>
            <ul>
              {providers.map((provider) => (
                <li key={provider}>{provider}</li>
              ))}
            </ul>
          </div>
        </section>

        <section className={styles.services} id="how-we-help" aria-labelledby="services-heading">
          <div className={styles.sectionHeading}>
            <p className={styles.eyebrow}>One mission, three ways to help</p>
            <h2 id="services-heading">Move from knowing the framework to doing the work.</h2>
            <p>
              Learn independently, equip your team, or bring in experienced
              support for the work in front of you.
            </p>
          </div>
          <div className={styles.serviceGrid}>
            {services.map((service) => (
              <article className={styles.serviceCard} key={service.title}>
                <div className={styles.serviceIcon} aria-hidden="true">
                  <img src={service.image} alt="" width="112" height="112" />
                </div>
                <p className={styles.cardEyebrow}>{service.eyebrow}</p>
                <h3>{service.title}</h3>
                <p>{service.copy}</p>
                <a href={service.href}>
                  {service.cta}
                  <span aria-hidden="true">→</span>
                </a>
              </article>
            ))}
          </div>
        </section>

        <section className={styles.method} aria-labelledby="method-heading">
          <div className={styles.methodIntro}>
            <p className={styles.eyebrow}>Built for durable understanding</p>
            <h2 id="method-heading">Training that connects controls to real systems.</h2>
            <p>
              Cyber risk expertise comes from connecting the reason behind a
              control to the way it works in technology. Our learning path makes
              that connection explicit.
            </p>
            <a href={ACADEMY_URL}>Browse the course catalog</a>
          </div>
          <ol className={styles.methodGrid}>
            {learningSteps.map((step) => (
              <li key={step.number}>
                <span>{step.number}</span>
                <h3>{step.title}</h3>
                <p>{step.copy}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className={styles.finalCta} aria-labelledby="final-cta-heading">
          <div className={styles.finalCtaCopy}>
            <p className={styles.eyebrow}>Your next audit can feel different</p>
            <h2 id="final-cta-heading">Become the person who can connect the control, the cloud, and the risk.</h2>
            <p>Start with a focused course and build from there.</p>
            <div className={styles.finalActions}>
              <a className={styles.primaryAction} href={ACADEMY_URL}>
                Start learning
                <span aria-hidden="true">→</span>
              </a>
              <a className={styles.darkSecondaryAction} href={ACADEMY_LOGIN_URL}>
                Academy login
              </a>
            </div>
          </div>
          <img src={GraduateOwl} alt="" width="260" height="319" />
        </section>
      </div>
    </Layout>
  );
};

export default Home;
